import { ActionIcon, Menu, useComputedColorScheme, useMantineColorScheme } from '@mantine/core'
import { useColorScheme } from '@mantine/hooks'

/**
 * Sun and moon, drawn here rather than pulled from an icon set.
 *
 * Two glyphs do not justify a dependency, and these are the only icons in the
 * product — everything else is a word, because a label is unambiguous and an
 * icon rarely is. A theme switch is the exception: it is a permanent fixture in
 * a corner where a word would be clutter, and sun/moon is understood without
 * being taught.
 */
function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  )
}

/**
 * Light, dark, or whatever the machine is doing.
 *
 * Three settings rather than a two-way switch, because "follow the system" is a
 * real preference and not the same as whichever value the system happens to
 * hold right now — someone whose laptop turns dark at sunset wants the app to
 * follow, not to be pinned to the value it had when they last looked.
 *
 * The trigger shows the scheme in force, so it always describes what you are
 * looking at. The System option shows what the system would give you at this
 * moment, which is the only way to tell what choosing it would do — a fixed
 * icon there would be a guess half the time.
 *
 * Mantine persists the choice; nothing here needs to.
 */
export function ColorSchemeToggle() {
  const { colorScheme, setColorScheme } = useMantineColorScheme()
  // What is actually rendering, with 'auto' already resolved.
  const inForce = useComputedColorScheme('light')
  // What the machine is asking for, regardless of what is set here.
  const system = useColorScheme()

  const label =
    colorScheme === 'auto' ? `Theme: system (currently ${system})` : `Theme: ${colorScheme}`

  return (
    <Menu shadow="md" width={190} position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size="lg" aria-label={label} title={label}>
          {inForce === 'dark' ? <MoonIcon /> : <SunIcon />}
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Theme</Menu.Label>
        <Menu.Item
          leftSection={<SunIcon />}
          onClick={() => setColorScheme('light')}
          aria-current={colorScheme === 'light'}
          rightSection={colorScheme === 'light' ? <Tick /> : null}
        >
          Light
        </Menu.Item>
        <Menu.Item
          leftSection={<MoonIcon />}
          onClick={() => setColorScheme('dark')}
          aria-current={colorScheme === 'dark'}
          rightSection={colorScheme === 'dark' ? <Tick /> : null}
        >
          Dark
        </Menu.Item>
        <Menu.Item
          // Whatever the machine is doing right now, so the icon says what
          // picking this would actually give you.
          leftSection={system === 'dark' ? <MoonIcon /> : <SunIcon />}
          onClick={() => setColorScheme('auto')}
          aria-current={colorScheme === 'auto'}
          rightSection={colorScheme === 'auto' ? <Tick /> : null}
        >
          System
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}

function Tick() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}
